import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { FlaskConical, LogOut, User, MapPin, Briefcase, Award, CheckCircle, ExternalLink, Loader2, BarChart3, Eye, Download, BookOpen, TrendingUp, FileText, Bell, X, ArrowRight } from 'lucide-react';
import { api } from '@/api/api';
import { toast } from 'sonner';
import { ThemeToggle } from '@/components/ThemeToggle';
import { authStorage } from '@/utils/authStorage';

const AuthorDashboard = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [possibleArticles, setPossibleArticles] = useState([]);
  const [myArticles, setMyArticles] = useState([]);
  const [metrics, setMetrics] = useState(null);
  const [loadingPossible, setLoadingPossible] = useState(true);
  const [loadingMy, setLoadingMy] = useState(true);
  const [loadingMetrics, setLoadingMetrics] = useState(true);
  const [claimingArticleId, setClaimingArticleId] = useState(null);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [showNotifications, setShowNotifications] = useState(false);

  useEffect(() => {
    // Check authentication and load user data
    const isAuthenticated = authStorage.isAuthorAuthenticated();
    if (!isAuthenticated) {
      navigate('/author/login');
      return;
    }

    // ALWAYS fetch fresh user data from backend
    loadUserDataFromBackend();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigate]);
  
  const loadUserDataFromBackend = async () => {
    try {
      // Get user ID from authStorage
      const userData = authStorage.getAuthorUser();
      if (!userData) {
        navigate('/author/login');
        return;
      }
      
      const parsedUser = userData;
      const userId = parsedUser.id;
      
      // Fetch fresh data from backend
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${API_URL}/api/auth/me?user_id=${userId}`);
      
      if (!response.ok) {
        throw new Error('Failed to fetch user data');
      }
      
      const freshUserData = await response.json();
      
      // Update authStorage with fresh data
      authStorage.updateAuthorUser(freshUserData);
      
      // Update state
      setUser(freshUserData);
      
      // Load articles and metrics
      loadPossibleArticles(freshUserData.id);
      loadMyArticles(freshUserData.id);
      loadMetrics(freshUserData.id);
      loadNotifications(freshUserData.id);
      
    } catch (error) {
      void 0;
      authStorage.clearAuthorAuth();
      toast.error('Session expired. Please log in again.');
      navigate('/author/login');
    }
  };

  const loadMetrics = async (userId) => {
    setLoadingMetrics(true);
    try {
      const response = await api.getAuthorMetrics(userId);
      setMetrics(response.metrics);
    } catch (error) {
      void 0;
    } finally {
      setLoadingMetrics(false);
    }
  };

  const loadNotifications = async (userId) => {
    try {
      const response = await api.getNotifications(userId);
      setNotifications(response.notifications || []);
      setUnreadCount(response.unread_count || 0);
    } catch (error) {
      void 0;
    }
  };

  const handleMarkRead = async (notifId) => {
    try {
      await api.markNotificationRead(notifId);
      setNotifications(prev => prev.map(n => n.id === notifId ? { ...n, read: true } : n));
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (error) {
      void 0;
    }
  };

  const handleMarkAllRead = async () => {
    if (!user) return;
    try {
      await api.markAllNotificationsRead(user.id);
      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
      setUnreadCount(0);
    } catch (error) {
      void 0;
    }
  };

  const handleTestAlert = async () => {
    if (!user) return;
    try {
      await api.testCitationAlert(user.id);
      toast.success('Test citation alert created!');
      loadNotifications(user.id);
    } catch (error) {
      toast.error('Failed to create test alert');
    }
  };

  const loadPossibleArticles = async (userId) => {
    setLoadingPossible(true);
    try {
      const response = await api.getPossibleArticles(userId);
      setPossibleArticles(response.articles || []);
    } catch (error) {
      void 0;
    } finally {
      setLoadingPossible(false);
    }
  };

  const loadMyArticles = async (userId) => {
    setLoadingMy(true);
    try {
      const response = await api.getMyArticles(userId);
      setMyArticles(response.articles || []);
    } catch (error) {
      void 0;
    } finally {
      setLoadingMy(false);
    }
  };

  const handleClaimArticle = async (articleId) => {
    setClaimingArticleId(articleId);
    try {
      await api.claimArticle(user.id, articleId);
      toast.success('Article claimed successfully!');
      
      // Refresh lists and metrics
      loadPossibleArticles(user.id);
      loadMyArticles(user.id);
      loadMetrics(user.id);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to claim article');
    } finally {
      setClaimingArticleId(null);
    }
  };

  const handleLogout = () => {
    authStorage.clearAuthorAuth();
    navigate('/author/login');
  };

  if (!user) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p>Loading...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Navigation */}
      <nav className="bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between h-16 items-center">
            <Link to="/" className="flex items-center space-x-2">
              <FlaskConical className="h-7 w-7 sm:h-8 sm:w-8 text-blue-600" />
              <span className="text-lg sm:text-xl font-bold text-slate-900">Author Dashboard</span>
            </Link>
            <div className="flex items-center gap-2 sm:gap-4">
              <span className="hidden sm:inline text-sm text-slate-600">{user.email}</span>
              {/* Notification Bell */}
              <div className="relative">
                <Button variant="ghost" size="sm" onClick={() => setShowNotifications(!showNotifications)} data-testid="notification-bell">
                  <Bell className="h-5 w-5" />
                  {unreadCount > 0 && (
                    <span className="absolute -top-0.5 -right-0.5 bg-red-500 text-white text-[10px] font-bold rounded-full w-4.5 h-4.5 flex items-center justify-center min-w-[18px] min-h-[18px] px-1" data-testid="unread-badge">
                      {unreadCount}
                    </span>
                  )}
                </Button>
              </div>
              <Link to="/" className="hidden sm:inline">
                <Button variant="ghost" size="sm">Home</Button>
              </Link>
              <Button variant="outline" size="sm" onClick={handleLogout}>
                <LogOut className="mr-1 sm:mr-2 h-4 w-4" />
                <span className="hidden sm:inline">Logout</span>
                <span className="sm:hidden">Exit</span>
              </Button>
              <ThemeToggle />
            </div>
          </div>
        </div>
      </nav>

      {/* Notification Panel */}
      {showNotifications && (
        <div className="fixed inset-0 z-50 flex justify-end" data-testid="notification-panel">
          <div className="absolute inset-0 bg-black/20" onClick={() => setShowNotifications(false)} />
          <div className="relative w-full max-w-md bg-white dark:bg-slate-900 shadow-2xl border-l border-slate-200 dark:border-slate-700 flex flex-col h-full animate-in slide-in-from-right">
            {/* Panel Header */}
            <div className="flex items-center justify-between px-5 py-4 border-b border-slate-200 dark:border-slate-700">
              <div className="flex items-center gap-2">
                <Bell className="h-5 w-5 text-slate-600 dark:text-slate-400" />
                <h2 className="text-lg font-bold text-slate-900 dark:text-white">Citation Alerts</h2>
                {unreadCount > 0 && (
                  <Badge variant="destructive" className="text-xs">{unreadCount} new</Badge>
                )}
              </div>
              <div className="flex items-center gap-1">
                {unreadCount > 0 && (
                  <Button variant="ghost" size="sm" onClick={handleMarkAllRead} className="text-xs text-blue-600" data-testid="mark-all-read-btn">
                    Mark all read
                  </Button>
                )}
                <Button variant="ghost" size="sm" onClick={() => setShowNotifications(false)}>
                  <X className="h-4 w-4" />
                </Button>
              </div>
            </div>

            {/* Panel Body */}
            <div className="flex-1 overflow-y-auto">
              {notifications.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-64 text-slate-400 dark:text-slate-500">
                  <Bell className="h-12 w-12 mb-3 opacity-30" />
                  <p className="text-sm font-medium">No citation alerts yet</p>
                  <p className="text-xs mt-1 text-center px-8">You'll be notified when someone cites your research articles</p>
                </div>
              ) : (
                <div className="divide-y divide-slate-100 dark:divide-slate-800">
                  {notifications.map(notif => (
                    <div
                      key={notif.id}
                      className={`px-5 py-4 hover:bg-slate-50 dark:hover:bg-slate-800/50 cursor-pointer transition-colors ${!notif.read ? 'bg-blue-50/50 dark:bg-blue-950/20 border-l-3 border-l-blue-500' : ''}`}
                      onClick={() => !notif.read && handleMarkRead(notif.id)}
                      data-testid={`notification-${notif.id}`}
                    >
                      <div className="flex items-start gap-3">
                        <div className={`mt-1 w-2 h-2 rounded-full flex-shrink-0 ${!notif.read ? 'bg-blue-500' : 'bg-transparent'}`} />
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            <BookOpen className="h-3.5 w-3.5 text-amber-500 flex-shrink-0" />
                            <span className="text-xs font-bold text-amber-600 dark:text-amber-400 uppercase tracking-wider">{notif.title}</span>
                          </div>
                          <p className="text-sm text-slate-700 dark:text-slate-300 leading-relaxed">{notif.message}</p>
                          {notif.data?.citing_journal && (
                            <p className="text-xs text-slate-400 mt-1.5">{notif.data.citing_journal}{notif.data.citing_year ? ` (${notif.data.citing_year})` : ''}</p>
                          )}
                          {notif.data?.cited_article_id && (
                            <Link
                              to={`/article/${notif.data.cited_article_id}`}
                              className="inline-flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800 mt-2 font-medium"
                              onClick={(e) => e.stopPropagation()}
                            >
                              View your article <ArrowRight className="h-3 w-3" />
                            </Link>
                          )}
                          <p className="text-[11px] text-slate-400 mt-1.5">
                            {new Date(notif.created_at).toLocaleDateString('bg-BG', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                          </p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Panel Footer */}
            <div className="px-5 py-3 border-t border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/50">
              <Button variant="outline" size="sm" className="w-full text-xs" onClick={handleTestAlert} data-testid="test-alert-btn">
                <Bell className="mr-1.5 h-3.5 w-3.5" />
                Send Test Citation Alert
              </Button>
            </div>
          </div>
        </div>
      )}

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-8">
        {/* Welcome Section */}
        <div className="mb-6 sm:mb-8">
          <h1 className="text-2xl sm:text-3xl font-bold text-slate-900 mb-2">
            Welcome, {user.username}!
          </h1>
          <p className="text-slate-600">
            Manage your author profile and claim your research articles
          </p>
        </div>

        {/* Profile Card */}
        <Card className="mb-8">
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="flex items-center">
                <User className="mr-2 h-5 w-5" />
                Your Profile
              </CardTitle>
              <Link to="/author/profile/edit">
                <Button variant="outline" size="sm">
                  <User className="mr-2 h-4 w-4" />
                  Edit Profile
                </Button>
              </Link>
            </div>
          </CardHeader>
          <CardContent>
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <p className="text-sm text-slate-600 mb-1">Username</p>
                <p className="font-semibold">{user.username}</p>
              </div>

              <div>
                <p className="text-sm text-slate-600 mb-1">Email</p>
                <p className="font-semibold">{user.email}</p>
              </div>

              <div className="flex items-center gap-2">
                <MapPin className="h-4 w-4 text-slate-500" />
                <div>
                  <p className="text-sm text-slate-600 mb-1">Location</p>
                  <p className="font-semibold">{user.town}, {user.country}</p>
                </div>
              </div>

              <div className="flex items-center gap-2">
                <Briefcase className="h-4 w-4 text-slate-500" />
                <div>
                  <p className="text-sm text-slate-600 mb-1">Occupation</p>
                  <p className="font-semibold">{user.occupation}</p>
                </div>
              </div>

              <div className="flex items-center gap-2">
                <Award className="h-4 w-4 text-slate-500" />
                <div>
                  <p className="text-sm text-slate-600 mb-1">Researcher Role</p>
                  <p className="font-semibold">{user.researcher_role}</p>
                </div>
              </div>
              
              {/* Name Details */}
              {(user.first_name || user.middle_name || user.last_name) && (
                <div className="md:col-span-2 border-t pt-4 mt-2">
                  <p className="text-sm text-slate-600 mb-2 font-semibold">Full Name Details:</p>
                  <div className="flex flex-wrap gap-2">
                    {user.first_name && (
                      <span className="px-3 py-1 bg-blue-50 text-blue-700 rounded-full text-sm">
                        First: {user.first_name}
                      </span>
                    )}
                    {user.middle_name && (
                      <span className="px-3 py-1 bg-blue-50 text-blue-700 rounded-full text-sm">
                        Middle: {user.middle_name}
                      </span>
                    )}
                    {user.last_name && (
                      <span className="px-3 py-1 bg-blue-50 text-blue-700 rounded-full text-sm">
                        Last: {user.last_name}
                      </span>
                    )}
                  </div>
                </div>
              )}
              
              {/* Alternative Names */}
              {user.alternative_names && user.alternative_names.length > 0 && (
                <div className="md:col-span-2 border-t pt-4 mt-2">
                  <p className="text-sm text-slate-600 mb-2 font-semibold">Alternative Names:</p>
                  <div className="flex flex-wrap gap-2">
                    {user.alternative_names.map((name, idx) => (
                      <span key={idx} className="px-3 py-1 bg-purple-50 text-purple-700 rounded-full text-sm">
                        {name}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Author Impact Metrics */}
        <Card className="mb-8">
          <CardHeader>
            <CardTitle>Your Impact Metrics</CardTitle>
          </CardHeader>
          <CardContent>
            {loadingMetrics ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-6 w-6 animate-spin text-blue-600" />
                <span className="ml-2 text-slate-600">Loading metrics...</span>
              </div>
            ) : (
              <>
                <div className="grid grid-cols-2 md:grid-cols-5 gap-3 sm:gap-4 mb-6">
                  {/* Total Articles */}
                  <div className="text-center p-3 sm:p-5 bg-blue-50 rounded-lg border-2 border-blue-200" data-testid="metric-articles">
                    <BarChart3 className="h-5 w-5 sm:h-7 sm:w-7 text-blue-600 mx-auto mb-1" />
                    <div className="text-xl sm:text-3xl font-bold text-blue-900 mb-1">
                      {metrics?.total_articles || 0}
                    </div>
                    <p className="text-xs text-blue-800 font-semibold">Articles</p>
                  </div>

                  {/* Average Score */}
                  <div className="text-center p-3 sm:p-5 bg-purple-50 rounded-lg border-2 border-purple-200" data-testid="metric-avg-score">
                    <Award className="h-5 w-5 sm:h-7 sm:w-7 text-purple-600 mx-auto mb-1" />
                    <div className="text-xl sm:text-3xl font-bold text-purple-900 mb-1">
                      {metrics?.average_methodology_score || 0}
                    </div>
                    <p className="text-xs text-purple-800 font-semibold">Avg Score</p>
                  </div>

                  {/* Total Views */}
                  <div className="text-center p-3 sm:p-5 bg-green-50 rounded-lg border-2 border-green-200" data-testid="metric-views">
                    <Eye className="h-5 w-5 sm:h-7 sm:w-7 text-green-600 mx-auto mb-1" />
                    <div className="text-xl sm:text-3xl font-bold text-green-900 mb-1">
                      {metrics?.total_views || 0}
                    </div>
                    <p className="text-xs text-green-800 font-semibold">Views</p>
                  </div>

                  {/* Total Downloads */}
                  <div className="text-center p-3 sm:p-5 bg-cyan-50 rounded-lg border-2 border-cyan-200" data-testid="metric-downloads">
                    <Download className="h-5 w-5 sm:h-7 sm:w-7 text-cyan-600 mx-auto mb-1" />
                    <div className="text-xl sm:text-3xl font-bold text-cyan-900 mb-1">
                      {metrics?.total_downloads || 0}
                    </div>
                    <p className="text-xs text-cyan-800 font-semibold">Downloads</p>
                  </div>

                  {/* Total Citations */}
                  <div className="text-center p-3 sm:p-5 bg-amber-50 rounded-lg border-2 border-amber-200 col-span-2 md:col-span-1" data-testid="metric-citations">
                    <BookOpen className="h-5 w-5 sm:h-7 sm:w-7 text-amber-600 mx-auto mb-1" />
                    <div className="text-xl sm:text-3xl font-bold text-amber-900 mb-1">
                      {metrics?.total_citations || 0}
                    </div>
                    <p className="text-xs text-amber-800 font-semibold">Citations</p>
                  </div>
                </div>

                {/* Per-Article Breakdown */}
                {metrics?.article_breakdown && metrics.article_breakdown.length > 0 && (
                  <div className="mt-6" data-testid="article-breakdown">
                    <h3 className="text-sm font-bold text-slate-800 uppercase tracking-wider mb-3 flex items-center gap-2">
                      <FileText className="h-4 w-4 text-slate-500" />
                      Per-Article Performance
                    </h3>
                    <div className="overflow-x-auto -mx-4 sm:mx-0">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="border-b-2 border-slate-200">
                            <th className="text-left py-2 px-3 text-xs font-semibold text-slate-500 uppercase">Article</th>
                            <th className="text-center py-2 px-2 text-xs font-semibold text-slate-500 uppercase whitespace-nowrap">Score</th>
                            <th className="text-center py-2 px-2 text-xs font-semibold text-slate-500 uppercase">Views</th>
                            <th className="text-center py-2 px-2 text-xs font-semibold text-slate-500 uppercase whitespace-nowrap">Downloads</th>
                            <th className="text-center py-2 px-2 text-xs font-semibold text-slate-500 uppercase whitespace-nowrap">Citations</th>
                          </tr>
                        </thead>
                        <tbody>
                          {metrics.article_breakdown.map((ab, idx) => (
                            <tr key={ab.article_id} className={`border-b border-slate-100 hover:bg-slate-50 ${idx === 0 ? 'bg-yellow-50/50' : ''}`}>
                              <td className="py-2.5 px-3 max-w-[280px]">
                                <Link to={`/article/${ab.article_id}`} className="text-blue-600 hover:text-blue-800 hover:underline font-medium line-clamp-2 text-sm leading-snug">
                                  {ab.title}
                                </Link>
                                <div className="flex items-center gap-1.5 mt-0.5">
                                  <span className="text-xs text-slate-400">{ab.journal}</span>
                                  {ab.year && <span className="text-xs text-slate-400">({ab.year})</span>}
                                </div>
                              </td>
                              <td className="text-center py-2.5 px-2">
                                <span className="inline-flex items-center justify-center w-10 h-10 rounded-full bg-purple-100 text-purple-700 font-bold text-sm">
                                  {ab.methodology_score || '—'}
                                </span>
                              </td>
                              <td className="text-center py-2.5 px-2">
                                <span className="font-semibold text-slate-700">{ab.views || 0}</span>
                              </td>
                              <td className="text-center py-2.5 px-2">
                                <span className="font-semibold text-slate-700">{ab.downloads || 0}</span>
                              </td>
                              <td className="text-center py-2.5 px-2">
                                <span className="font-semibold text-slate-700">{ab.citations || 0}</span>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}

                {/* Top Article */}
                {metrics?.top_article && (
                  <div className="mt-6 p-6 bg-gradient-to-r from-yellow-50 to-amber-50 rounded-lg border-2 border-yellow-300">
                    <div className="flex items-center gap-2 mb-3">
                      <TrendingUp className="h-6 w-6 text-yellow-700" />
                      <h3 className="text-lg font-bold text-yellow-900">Your Top Article</h3>
                    </div>
                    <h4 className="font-semibold text-slate-900 mb-2">
                      {metrics.top_article.title}
                    </h4>
                    <div className="flex flex-wrap gap-2 mb-3">
                      <Badge className="bg-yellow-600">
                        Methodology Score: {metrics.top_article.methodology_score}
                      </Badge>
                      <Badge variant="outline">{metrics.top_article.journal}</Badge>
                      <Badge variant="outline">{metrics.top_article.year}</Badge>
                      <Badge className="bg-purple-600">
                        {metrics.top_article.research_impact}
                      </Badge>
                    </div>
                    <Link to={`/research-insight/${metrics.top_article.article_id}`}>
                      <Button variant="outline" size="sm">
                        <ExternalLink className="mr-2 h-4 w-4" />
                        View Full Analysis
                      </Button>
                    </Link>
                  </div>
                )}
              </>
            )}
          </CardContent>
        </Card>

        {/* Possible Articles by You */}
        <Card className="mb-8">
          <CardHeader>
            <CardTitle>Possible Articles by You</CardTitle>
            <p className="text-sm text-slate-600 mt-2">
              Articles where author names match yours (fuzzy match). Click "Claim" to add them to your profile.
            </p>
          </CardHeader>
          <CardContent>
            {loadingPossible ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-6 w-6 animate-spin text-blue-600" />
                <span className="ml-2 text-slate-600">Searching for your articles...</span>
              </div>
            ) : possibleArticles.length === 0 ? (
              <p className="text-slate-600 text-center py-8">
                No matching articles found. Articles are matched based on author names in the database.
              </p>
            ) : (
              <div className="space-y-4">
                {possibleArticles.map((item, index) => (
                  <div key={item.article?.id || `ab-${index}`} className="border rounded-lg p-4 hover:bg-slate-50">
                    <div className="flex justify-between items-start gap-4">
                      <div className="flex-1">
                        <h4 className="font-semibold text-slate-900 mb-2">
                          {item.article.title}
                        </h4>
                        <div className="flex flex-wrap gap-2 mb-2">
                          <Badge variant="outline">{item.article.journal || 'Unknown Journal'}</Badge>
                          <Badge variant="outline">{item.article.publication_year || 'N/A'}</Badge>
                          <Badge className="bg-green-100 text-green-800">
                            {item.match_score}% match with "{item.matched_author_name}"
                          </Badge>
                        </div>
                        {item.article.doi && (
                          <p className="text-sm text-slate-600">
                            DOI: {item.article.doi}
                          </p>
                        )}
                      </div>
                      <Button
                        onClick={() => handleClaimArticle(item.article.id)}
                        disabled={claimingArticleId === item.article.id}
                        size="sm"
                      >
                        {claimingArticleId === item.article.id ? (
                          <>
                            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                            Claiming...
                          </>
                        ) : (
                          <>
                            <CheckCircle className="mr-2 h-4 w-4" />
                            Claim
                          </>
                        )}
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* My Articles */}
        <Card>
          <CardHeader>
            <CardTitle>My Articles ({myArticles.length})</CardTitle>
            <p className="text-sm text-slate-600 mt-2">
              Articles you have claimed
            </p>
          </CardHeader>
          <CardContent>
            {loadingMy ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-6 w-6 animate-spin text-blue-600" />
                <span className="ml-2 text-slate-600">Loading your articles...</span>
              </div>
            ) : myArticles.length === 0 ? (
              <p className="text-slate-600 text-center py-8">
                You haven't claimed any articles yet. Check "Possible Articles by You" above.
              </p>
            ) : (
              <div className="space-y-4">
                {myArticles.map((item, index) => (
                  <div key={item.article?.id || `my-${index}`} className="border rounded-lg p-4">
                    <h4 className="font-semibold text-slate-900 mb-2">
                      {item.article.title}
                    </h4>
                    <div className="flex flex-wrap gap-2 mb-2">
                      <Badge variant="outline">{item.article.journal || 'Unknown Journal'}</Badge>
                      <Badge variant="outline">{item.article.publication_year || 'N/A'}</Badge>
                      <Badge className="bg-blue-100 text-blue-800">
                        Claimed {new Date(item.claimed_at).toLocaleDateString()}
                      </Badge>
                    </div>
                    {item.article.authors && (
                      <p className="text-sm text-slate-600 mb-2">
                        Authors: {item.article.authors.join(', ')}
                      </p>
                    )}
                    <Link to={`/research-insight/${item.article.id}`}>
                      <Button variant="outline" size="sm">
                        <ExternalLink className="mr-2 h-4 w-4" />
                        View Details
                      </Button>
                    </Link>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default AuthorDashboard;
